'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useReportChat, useReportChatReasons } from '@/slw/entities/chat';
import { ReportChatFormValues, reportChatSchema } from '@/slw/entities/chat/chat.schema';
import { useChatStore } from '@/slw/shared/lib/firebase/chat-store';
import { useAuthStore } from '@/slw/shared/stores';
import { Button } from '@/slw/shared/ui/button';
import { Dialog, DialogContent } from '@/slw/shared/ui/dialog';
import FormProvider, { Select, Textarea } from '@/slw/shared/ui/form';

export function ReportChatModal({
  open,
  onOpenChange,
  isLoading,
}: {
  open: boolean;
  isLoading?: boolean;
  onOpenChange: () => void;
}) {
  const { user } = useAuthStore();
  const { chatId, user: userChat } = useChatStore();
  const reportChatMutation = useReportChat();
  const { data: reportChatReasons } = useReportChatReasons();

  const form = useForm<ReportChatFormValues>({
    resolver: zodResolver(reportChatSchema),
    defaultValues: {
      reportReason: '',
      reportDescription: '',
    },
  });

  const onSubmit = async (data: ReportChatFormValues) => {
    await reportChatMutation?.mutateAsync({
      ...data,
      reporterId: user.id!,
      reportUserId: userChat?.id || '',
      reportChatId: chatId!,
    });
    onOpenChange();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='md:max-w-[668px]  overflow-y-scroll md:min-w-[40rem] gap-4 max-h-screen md:max-h-[900px]'
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <h2 className='text-2xl font-bold mt-3'>Report Chat</h2>
        <FormProvider {...form} onSubmit={onSubmit}>
          <div className='flex flex-col gap-4'>
            <Select
              name='reportReason'
              label='Report reason'
              required
              placeholder='Enter your report reason'
              direction='column'
              options={
                reportChatReasons?.map((item) => ({
                  label: item.name,
                  value: item.id,
                })) || []
              }
            />
            <Textarea name='reportDescription' label='More description' placeholder='' direction='column' rows={5} />
          </div>
          <div className='flex gap-1'>
            <Button
              onClick={onOpenChange}
              color='secondary'
              variant='outline'
              className='w-full mt-4'
              disabled={isLoading}
            >
              CANCEL
            </Button>
            <Button type='submit' color='primary' variant='default' className='w-full mt-4' disabled={isLoading}>
              {isLoading ? 'Submitting...' : 'SUBMIT'}
            </Button>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
